import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
// GridToolbarContainer (the root of every screen's toolbar) calls
// useGridRootProps(), which THROWS outside a mounted DataGrid. Card mode has no
// grid, so the wrapper supplies a minimal root-props context around the
// screen's toolbar. Deep import is required: MUI X v7 does not export this
// context from the package root or from the `internals` entry point, and the
// webpack build resolves the package to these same root files (`module:
// ./index.js`), so the context instance is the one GridToolbarContainer reads.
import { GridRootPropsContext } from "@mui/x-data-grid/context/GridRootPropsContext.js";
import {
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Menu,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";

/**
 * DataGridResponsive
 *
 * A drop-in wrapper around MUI's <DataGrid>:
 *  - Desktop (>= 600px): renders the real grid with every prop passed through
 *    untouched — byte-identical to `<DataGrid {...props} />`.
 *  - Mobile (< 600px): renders a card list built from the same `columns` /
 *    `rows`, reusing each column's valueGetter / valueFormatter / renderCell,
 *    so chips, colored dates and every other cell rendering keeps its desktop
 *    look. Selection, long-press, the quick filter and pagination are wired to
 *    the screen's own state (filterModel / sortModel / selection model), so
 *    filters and sort survive the desktop <-> mobile switch.
 *
 * Mobile-only config lives in the `mobile` prop. See the Batch C decision doc
 * (.squad/decisions/inbox/chromie-datagridresponsive-batchc.md).
 *
 * ⚠️ The desktop branch must stay `<DataGrid {...props} />` — every prop
 * (sx, slots, slotProps, apiRef, onStateChange, …) is passed straight through.
 */

const MOBILE_QUERY = "(min-width:600px)";
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_COLLAPSED_AFTER = 5;
const MULTILINE_CHAR_THRESHOLD = 48;
const LONG_PRESS_CANCEL_PX = 8;

// ── Batch C wave 2 — card-mode bottom sheets (sort / filter) ────────────────
// zIndex: the sheets keep the MUI Drawer default (= theme.zIndex.drawer, 1200
// in this app — the theme does not customize zIndex). The screens'
// ColumnFilter / ColumnDateFilter poppers render at zIndex 9999 with their own
// transparent click-away backdrop at 9998 (verified in ColumnFilter.jsx
// L339/L329 and ColumnDateFilter.jsx L464/L454), so any popper opened from
// inside a sheet paints ABOVE it — the sheet stays strictly below, which is
// exactly the required stacking.
const SHEET_MAX_HEIGHT = "70vh";
const SHEET_PAPER_SX = {
  maxHeight: SHEET_MAX_HEIGHT,
  borderTopLeftRadius: "12px",
  borderTopRightRadius: "12px",
  borderTop: 1,
  borderRight: "none",
  borderBottom: "none",
  borderLeft: "none",
  borderColor: "divider",
};

// Filter chips (§3b): a generic search chip plus the screen-supplied
// `mobile.chips` ({ key, label, onClear }). Shown only while at least one chip
// exists. Wrapper deviation: the row sits in the pinned control stack rather
// than scrolling away with the cards, so the "you are filtered" signal stays
// visible (recorded in the wave-2 decision file).
const CARD_CHIP_SX = {
  maxWidth: "100%",
  "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
};

// Any tap that originates on one of these must never open the card overlay.
const INTERACTIVE_TARGET_SELECTOR = [
  "button",
  "a",
  "input",
  "select",
  "textarea",
  "[role='button']",
  "[role='link']",
  "[role='switch']",
  "[role='checkbox']",
  "[role='menuitem']",
  "[role='tab']",
].join(", ");

// Scroll positions are remembered per screen path so returning from a detail
// overlay restores where the user was. Module-level on purpose: the component
// unmounts during navigation, so instance refs cannot hold it.
const CARD_SCROLL_MEMORY = new Map();

const SKELETON_LINE_WIDTHS = [100, 85, 70, 90, 60];

// Minimal shape GridToolbarContainer needs (it only reads `classes`); anything
// grid-bound beyond the toolbar container must be swapped out by the screen in
// card mode (see Invoices' QuickFilterField).
const CARD_MODE_ROOT_PROPS = { classes: undefined, slots: {}, slotProps: {} };

// Card-mode quick filter matching mirrors the grid's `ignoreDiacritics`.
const normalizeText = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

// Card-search companion for MONEY columns. MUI's quick filter engine calls
// `column.getApplyQuickFilterFn(filterValue, column, apiRef)` once per
// column/query and then invokes the returned applier as
// `(cellValue, row, column, apiRef)` for every row — verified in the installed
// @mui/x-data-grid 7.29.12 source
// (hooks/features/filter/gridFilterUtils.js → buildAggregatedQuickFilterApplier:
// `fn: getApplyQuickFilterFn(value, column, publicApiRef)` then
// `applier.fn(value, row, column, publicApiRef)`). Amount columns opt in with
// `getApplyQuickFilterFn: currencyQuickFilterFn` and get identical behavior on
// the desktop grid AND the wrapper's card search (rowMatchesQuickFilter honors
// this hook). Both sides are normalized by stripping "$", "," and whitespace,
// so typing "1500" matches "$1,500.00" — and "1,500" matches it too.
export const currencyQuickFilterFn = (filterValue) => {
  const rawNeedle = String(filterValue ?? "").toLowerCase();
  const needle = rawNeedle.replace(/[$,\s]/g, "");
  return (cellValue) => {
    if (cellValue === null || cellValue === undefined || cellValue === "N/A") {
      return false;
    }
    const haystack = String(cellValue).toLowerCase();
    if (needle === "") {
      // Query was pure punctuation (e.g. a lone "$") — fall back to a plain
      // contains so the search still behaves like text search.
      return haystack.includes(rawNeedle);
    }
    return haystack.replace(/[$,\s]/g, "").includes(needle);
  };
};

// MUI X v7 valueGetter signature: (value, row, column, apiRef)
// (the filters hook in this codebase calls it as (null, row) — screens ignore
// the first argument, so passing the raw field value stays faithful to the grid).
const getColumnValue = (col, row) => {
  if (!row || !col) return undefined;
  const rawValue = row[col.field];
  if (typeof col.valueGetter === "function") {
    return col.valueGetter(rawValue, row, col, null);
  }
  return rawValue;
};

const getColumnFormattedValue = (col, row, value) => {
  if (typeof col.valueFormatter === "function") {
    return col.valueFormatter(value, row, col, null);
  }
  return value;
};

const buildCellParams = (col, row, id, value, formattedValue) => ({
  id,
  row,
  value,
  field: col.field,
  colDef: col,
  formattedValue,
  api: null,
  hasFocus: false,
  tabIndex: -1,
  isEditable: false,
});

const compareColumnValues = (col, rowA, rowB, idOf) => {
  const valueA = getColumnValue(col, rowA);
  const valueB = getColumnValue(col, rowB);

  if (typeof col.sortComparator === "function") {
    return col.sortComparator(
      valueA,
      valueB,
      buildCellParams(col, rowA, idOf(rowA), valueA, valueA),
      buildCellParams(col, rowB, idOf(rowB), valueB, valueB),
    );
  }

  if (typeof valueA === "number" && typeof valueB === "number") {
    return valueA - valueB;
  }

  const stringA = valueA === null || valueA === undefined ? "" : String(valueA);
  const stringB = valueB === null || valueB === undefined ? "" : String(valueB);
  return stringA.localeCompare(stringB, undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const selectionIncludes = (model, id) => {
  if (!model) return false;
  const ids = Array.isArray(model) ? model : model.ids;
  if (!Array.isArray(ids)) return false;
  return ids.some((entry) => String(entry) === String(id));
};

const selectionToArray = (model) => {
  if (!model) return [];
  if (Array.isArray(model)) return model;
  return Array.isArray(model.ids) ? model.ids : [];
};

const stopIfInteractiveTarget = (event) => {
  const target = event?.target;
  const cell = event?.currentTarget;
  if (!target || typeof target.closest !== "function") return;
  const interactive = target.closest(INTERACTIVE_TARGET_SELECTOR);
  // Round-7 ROOT CAUSE (Amine's 4th "single tap does not open" report):
  // `closest` also walks UP past the tapped cell — and the card's own root
  // IS a native <button> (CardActionArea), which sits above EVERY field in
  // the card. A plain tap on a field value therefore matched the card root
  // itself, stopPropagation() fired, and the click never reached the card's
  // onClick. Only an interactive element INSIDE the tapped cell (a nested
  // link/button/chip) may block the card tap; ancestor matches must not.
  if (interactive && cell && interactive !== cell && cell.contains(interactive)) {
    event.stopPropagation();
  }
};

const SkeletonCard = () => (
  <Card variant="outlined" sx={{ borderRadius: 2, mb: 1, p: 1.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      <Skeleton variant="text" sx={{ fontSize: "1.125rem", width: "60%" }} />
      <Skeleton
        variant="rounded"
        width={56}
        height={20}
        sx={{ ml: "auto", borderRadius: "12px" }}
      />
    </Box>
    {SKELETON_LINE_WIDTHS.map((width, index) => (
      <Skeleton
        key={index}
        variant="text"
        sx={{ fontSize: "0.875rem", width: `${width}%` }}
      />
    ))}
  </Card>
);

// The wrapper's card-mode Sort / Filter buttons copy the screens' quick-filter
// and ⋮ button recipe (QuickFilterField iconOnly + every toolbar's Grid
// Options): 36×36 hit area, 12px radius, 1px border — so the merged control row
// paints one visually uniform cluster ([search][⋮][sort][filter]) instead of
// two mismatched rows. ~36px is also the size Amine referenced ("the same size
// as the quick filter and the 3 dots buttons").
const cardControlButtonSx = (theme, active) => ({
  height: 36,
  width: 36,
  color: active ? theme.palette.primary.main : theme.palette.grey.main,
  border: `1px solid ${
    active ? theme.palette.primary.main : theme.palette.divider
  }`,
  borderRadius: "12px",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },
});

/**
 * One card. Kept at module scope so its expander state survives parent
 * re-renders (a component defined inside the parent would remount each render)
 * and resets when the card itself unmounts (filter change, row removed).
 */
const MobileCard = ({
  rowId,
  row,
  headline,
  badge,
  collapsedContent,
  expandedContent,
  expandedCount,
  rowHandlers,
  showCheckbox,
  selected,
  onToggleSelect,
  onCardTap,
  cardActions,
  contextMenuButton,
}) => {
  const [expanded, setExpanded] = useState(false);
  // Card ⋮ menu (`mobile.cardActions`, Amine 2026-10-05): optional per-row
  // actions rendered in a Menu anchored to a 28×28 trigger in the card's
  // top-right corner. The trigger is a SIBLING of the CardActionArea (not a
  // child), so pressing it can never open the card, start the screen's
  // long-press selection timer or fire the screen's row handlers. The screen
  // supplies a plain render function `(row, closeMenu) => ReactNode` that
  // returns MENU ITEMS; a null/undefined return hides the trigger for that
  // row. The function runs on every card render, so it must not use hooks.
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const closeActionsMenu = useCallback(() => setActionsAnchorEl(null), []);
  const actionsContent =
    typeof cardActions === "function" ? cardActions(row, closeActionsMenu) : null;
  const showActionsButton =
    actionsContent !== null && actionsContent !== undefined;
  // Card ⋮ context-menu trigger (`mobile.contextMenuButton`, Amine
  // 2026-10-05): touch devices have no right-click, so the ⋮ replays the
  // screen's own `onContextMenu` handler with a synthetic event whose
  // `currentTarget` is this button (it carries `data-id={rowId}`) and whose
  // clientX/clientY come from the button's rect — the SAME menu, items,
  // handlers and disabled states as the desktop right-click. Renders only
  // when the screen opted in AND a handler is actually wired.
  const showContextMenuButton =
    contextMenuButton === true && typeof rowHandlers?.onContextMenu === "function";
  const pressRef = useRef({ x: 0, y: 0, active: false });

  // 2026-10-05 blanket tap rule (Amine): one tap always runs the single-tap
  // path — the wrapper's `handleCardTap` decides between OPENING the row's
  // detail (selection not armed) and TOGGLING its selection (armed). The old
  // `mobile.doubleTapToOpen` routing (first tap selects / second tap opens)
  // was removed together with its last three screen callers.
  const handleCardClick = (event) => {
    onCardTap?.(event);
  };

  const cancelPress = useCallback(() => {
    pressRef.current.active = false;
  }, []);

  // Long-press hygiene: any pointer movement beyond 8px means "scroll/drag",
  // so the screen's own long-press timer is cancelled through its handlers.
  const handleTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (touch) {
      pressRef.current = { x: touch.clientX, y: touch.clientY, active: true };
    }
    rowHandlers?.onTouchStart?.(event);
  };

  const handleTouchMove = (event) => {
    if (!pressRef.current.active) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    if (
      Math.abs(touch.clientX - pressRef.current.x) > LONG_PRESS_CANCEL_PX ||
      Math.abs(touch.clientY - pressRef.current.y) > LONG_PRESS_CANCEL_PX
    ) {
      cancelPress();
      rowHandlers?.onTouchEnd?.(event);
    }
  };

  const handleTouchEnd = (event) => {
    cancelPress();
    rowHandlers?.onTouchEnd?.(event);
  };

  const handleTouchCancel = (event) => {
    cancelPress();
    (rowHandlers?.onTouchCancel ?? rowHandlers?.onTouchEnd)?.(event);
  };

  const handleMouseDown = (event) => {
    pressRef.current = { x: event.clientX, y: event.clientY, active: true };
    rowHandlers?.onMouseDown?.(event);
  };

  const handleMouseMove = (event) => {
    if (!pressRef.current.active) return;
    if (
      Math.abs(event.clientX - pressRef.current.x) > LONG_PRESS_CANCEL_PX ||
      Math.abs(event.clientY - pressRef.current.y) > LONG_PRESS_CANCEL_PX
    ) {
      cancelPress();
      rowHandlers?.onMouseUp?.(event);
    }
  };

  const handleMouseUp = (event) => {
    cancelPress();
    rowHandlers?.onMouseUp?.(event);
  };

  const handleMouseLeave = (event) => {
    cancelPress();
    (rowHandlers?.onMouseLeave ?? rowHandlers?.onMouseUp)?.(event);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        mb: 1,
        // The selected-row tint was REMOVED (Amine 2026-10-05 — "remove the
        // highlight option completely"): a mobile card never shows the
        // highlighted background; armed-mode selection is visible through
        // the checkbox only. `position: relative` anchors either top-right
        // ⋮ trigger.
        ...(showActionsButton || showContextMenuButton
          ? { position: "relative" }
          : {}),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        {showCheckbox && (
          <Box sx={{ display: "flex", alignItems: "flex-start", pl: 1.25, pr: 0.5, pt: 1.25 }}>
            <Checkbox
              size="small"
              checked={selected}
              onChange={() => onToggleSelect?.()}
              inputProps={{ "aria-label": `Select ${headline}` }}
              sx={{ p: 0.5 }}
            />
          </Box>
        )}
        <CardActionArea
          data-id={rowId}
          // Remove MUI's grey tap/focus highlight overlay — part 2 of the
          // "no card highlight" rule (Amine 2026-10-05; MUI v6.5 prop,
          // installed @mui/material version is 6.5.0).
          focusHighlight="none"
          onClick={handleCardClick}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          // Native long-press on a touch device fires the browser's
          // `contextmenu` event — on a card it must do NOTHING (Amine
          // 2026-10-05, round 6): the screen's context menu opens ONLY
          // through the ⋮ trigger (`mobile.contextMenuButton`), which replays
          // the screen's own handler. Forwarding it here made Android Chrome
          // open the menu IN ADDITION to arming the long-press selection.
          // Desktop right-click is untouched — the real grid rows keep
          // `slotProps.row.onContextMenu`.
          onContextMenu={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "flex-start",
            textAlign: "left",
            cursor: rowHandlers?.cursor || "pointer",
            userSelect: "none",
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              minWidth: 0,
              // Reserve the card ⋮ trigger column (12px edge + 28px button +
              // 4px gap = 44px from the card edge; the header already sits
              // 12px inside the CardActionArea padding) so the badge never
              // slides under an actions ⋮ OR context-menu ⋮ trigger.
              ...(showActionsButton || showContextMenuButton ? { pr: 4 } : {}),
            }}
          >
            <Typography
              variant="subtitle1"
              noWrap
              title={headline}
              sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}
            >
              {headline}
            </Typography>
            {badge}
          </Box>
          {collapsedContent}
          {/* Expanded fields stay INSIDE the tap area (2026-10-05): taps and
              double-taps on the revealed content now follow the exact same
              path as the collapsed body — before, the revealed fields were a
              dead zone (outside the CardActionArea), so double-click-to-open
              appeared to work only on the collapsed region. The expander
              BUTTON stays a sibling below, so its taps never open the card. */}
          {expandedCount > 0 && (
            <Collapse in={expanded} timeout={200}>
              <Box sx={{ pt: 0.25 }}>{expandedContent}</Box>
            </Collapse>
          )}
        </CardActionArea>
      </Box>

      {showActionsButton && (
        <IconButton
          size="small"
          aria-label={`Actions for ${headline}`}
          aria-haspopup="menu"
          aria-expanded={Boolean(actionsAnchorEl)}
          onClick={(event) => {
            event.stopPropagation();
            setActionsAnchorEl(event.currentTarget);
          }}
          onMouseDown={(event) => event.stopPropagation()}
          onTouchStart={(event) => event.stopPropagation()}
          sx={{
            position: "absolute",
            // If the screen also opted into the context-menu ⋮, stack this
            // actions trigger below it (the context menu owns the 12px slot).
            top: showContextMenuButton ? 48 : 12,
            right: 12,
            width: 28,
            height: 28,
            p: 0.25,
            zIndex: 1,
            color: "text.secondary",
          }}
        >
          <MoreVertRoundedIcon fontSize="small" />
        </IconButton>
      )}

      {showContextMenuButton && (
        <IconButton
          size="small"
          aria-label={`Open menu for ${headline}`}
          aria-haspopup="menu"
          data-id={rowId}
          onClick={(event) => {
            event.stopPropagation();
            const rect = event.currentTarget.getBoundingClientRect();
            // Replay the screen's right-click handler with a synthetic event
            // so the ⋮ opens THE SAME menu (identical items, handlers and
            // disabled states): the screens read `currentTarget` for the
            // row id and clientX/clientY for anchoring. The button itself is
            // the currentTarget and carries data-id={rowId}.
            rowHandlers.onContextMenu({
              preventDefault() {},
              currentTarget: event.currentTarget,
              clientX: rect.left + rect.width / 2,
              clientY: rect.bottom,
            });
          }}
          onMouseDown={(event) => event.stopPropagation()}
          onTouchStart={(event) => event.stopPropagation()}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 28,
            height: 28,
            p: 0.25,
            zIndex: 1,
            color: "text.secondary",
          }}
        >
          <MoreVertRoundedIcon fontSize="small" />
        </IconButton>
      )}

      {showActionsButton && (
        <Menu
          anchorEl={actionsAnchorEl}
          open={Boolean(actionsAnchorEl)}
          onClose={closeActionsMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {actionsContent}
        </Menu>
      )}

      {expandedCount > 0 && (
        <Button
          size="small"
          onClick={() => setExpanded((previous) => !previous)}
          aria-expanded={expanded}
          startIcon={
            expanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />
          }
          sx={{
            minHeight: 40,
            width: "100%",
            justifyContent: "flex-start",
            px: 1.5,
            textTransform: "none",
          }}
        >
          {expanded ? "Show less" : `Show ${expandedCount} more`}
        </Button>
      )}
    </Card>
  );
};

const CardsEmptyState = ({ filtered, localeText, onClearFilters }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      py: 6,
      px: 2,
      textAlign: "center",
    }}
  >
    {filtered ? (
      <FilterAltOffRoundedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
    ) : (
      <InboxRoundedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
    )}
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      {filtered
        ? localeText?.noResultsOverlayLabel ?? "No results found."
        : localeText?.noRowsLabel ?? "No rows"}
    </Typography>
    {filtered && typeof onClearFilters === "function" && (
      <Button
        variant="contained"
        size="small"
        onClick={onClearFilters}
        sx={{ mt: 1, textTransform: "none" }}
      >
        Clear filters
      </Button>
    )}
  </Box>
);

function DataGridResponsiveCards({
  mobile,
  rows,
  columns,
  getRowId,
  loading,
  filterModel,
  onFilterModelChange,
  sortModel,
  onSortModelChange,
  onStateChange,
  onRowClick,
  checkboxSelection,
  disableRowSelectionOnClick,
  rowSelectionModel,
  onRowSelectionModelChange,
  getCellClassName,
  slots,
  slotProps,
  sx,
  localeText,
  columnVisibilityModel,
}) {
  const pageSize = Number(mobile?.pageSize) > 0 ? Number(mobile.pageSize) : DEFAULT_PAGE_SIZE;
  const collapsedAfter =
    Number(mobile?.collapsedAfter) > 0
      ? Number(mobile.collapsedAfter)
      : DEFAULT_COLLAPSED_AFTER;
  const multilineFields = useMemo(
    () => (Array.isArray(mobile?.multilineFields) ? mobile.multilineFields : []),
    [mobile?.multilineFields],
  );
  const hiddenFields = useMemo(
    () => (Array.isArray(mobile?.hiddenFields) ? mobile.hiddenFields : []),
    [mobile?.hiddenFields],
  );
  // Round-7 (Amine 2026-10-05): a touch device replays SYNTHETIC mouse
  // events after every tap; forwarding the screens' mouse long-press
  // handlers to mobile cards made those ghosts restart the 500ms long-press
  // timer on every tap (pure interference). Coarse-pointer devices get the
  // touch handlers only; fine-pointer devices (incl. a desktop-narrow
  // card-mode window) keep the mouse long-press path untouched.
  const isCoarsePointer = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(pointer: coarse)")?.matches === true,
    [],
  );
  const selectable = mobile?.selectable !== false;
  // Opt-in card-content shaping (the 11 migrated screens keep today's defaults
  // unless they set these).
  //  - hideNAFields: drop body fields whose formatted value is the "N/A"
  //    placeholder, per row (Invoices' "Late By" — Amine 2026-10-01).
  //    `true` = every body field (legacy). An array of field names scopes the
  //    drop to exactly those fields (2026-10-05) — an unscoped drop can empty
  //    the expanded set per row, which hides the "Show N more" button (the
  //    expander renders only while ≥1 expanded field survives per row).
  //  - hideBodyFields: render only the headline + badge, no body rows and no
  //    "Show N more" expander (kept for backward compat; Linguist Payments
  //    migrated to `collapseBody` — Amine 2026-10-05).
  //  - collapseBody: keep the FULL body column set but fold ALL of it behind
  //    the "Show N more" expander — the collapsed card shows only the
  //    headline + badge (Linguist Payments — Amine 2026-10-05).
  //  - bodyOrder: explicit card-body field order — listed fields render first
  //    in this exact order, remaining body fields keep their grid column order
  //    after them. Used by Invoices (2026-10-05): the collapsed window stays
  //    Client / Late By / Amount while the expander reveals the date columns
  //    in desktop order.
  //  - selectionArmed: boolean overriding the derived "selection mode is on"
  //    signal (see below); only needed when a screen's checkbox visibility
  //    diverges from its selection mode.
  //  - cardActions: (row, closeMenu) => ReactNode — renders a ⋮ menu trigger
  //    in the card's top-right corner; the returned nodes render inside the
  //    Menu. See MobileCard for the full contract (Linguist Payments —
  //    Amine 2026-10-05).
  const hideNAFieldsAll = mobile?.hideNAFields === true;
  const hideNAFieldsList = useMemo(
    () => (Array.isArray(mobile?.hideNAFields) ? mobile.hideNAFields : null),
    [mobile?.hideNAFields],
  );
  const hideBodyFields = mobile?.hideBodyFields === true;
  const collapseBody = mobile?.collapseBody === true;
  const bodyOrder = useMemo(
    () => (Array.isArray(mobile?.bodyOrder) ? mobile.bodyOrder : null),
    [mobile?.bodyOrder],
  );
  const cardActions =
    typeof mobile?.cardActions === "function" ? mobile.cardActions : undefined;
  // `mobile.contextMenuButton` (Amine 2026-10-05): renders a ⋮ at the card's
  // top-right edge that replays the screen's right-click context-menu
  // handler — mobile has no right-click. See MobileCard for the contract.
  const contextMenuButton = mobile?.contextMenuButton === true;

  // ── Card-mode tap contract (2026-10-05 blanket rule, Amine) ─────────────
  // One tap on a card OPENS the row's detail on every screen; a tap only
  // TOGGLES selection while selection is ARMED (checkboxes showing). The
  // screen's `onRowClick` runs first and owns the open/select decision (see
  // `handleCardTap`); "armed" is the same signal that renders the checkboxes,
  // derived here so the wrapper's auto-toggle can never fire outside armed
  // selection. `mobile.selectionArmed` is the explicit override for screens
  // whose checkbox visibility differs from their selection mode.
  const selectionArmed =
    typeof mobile?.selectionArmed === "boolean"
      ? mobile.selectionArmed
      : Boolean(checkboxSelection) && selectable;

  const [internalQuickFilter, setInternalQuickFilter] = useState("");
  const [visibleCount, setVisibleCount] = useState(pageSize);
  // Batch C wave 2 — card-mode bottom-sheet visibility.
  const [sortSheetOpen, setSortSheetOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const scrollerRef = useRef(null);

  const rowsSafe = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const rowIdOf = useCallback(
    (row) => (typeof getRowId === "function" ? getRowId(row) : row?.id),
    [getRowId],
  );

  // ---------------------------------------------------------------------------
  // Columns (the `columns` prop already reflects the user's visibility choice on
  // screens like Invoices; columnVisibilityModel is respected when provided).
  // ---------------------------------------------------------------------------
  const visibleColumns = useMemo(
    () =>
      (Array.isArray(columns) ? columns : []).filter((col) => {
        if (!col || !col.field) return false;
        if (col.type === "actions") return false;
        if (String(col.field).startsWith("__")) return false;
        if (columnVisibilityModel && columnVisibilityModel[col.field] === false) {
          return false;
        }
        return true;
      }),
    [columns, columnVisibilityModel],
  );

  const primaryColumn = useMemo(() => {
    if (mobile?.primaryField) {
      const match = visibleColumns.find((col) => col.field === mobile.primaryField);
      if (match) return match;
    }
    return visibleColumns[0] ?? null;
  }, [visibleColumns, mobile?.primaryField]);

  const badgeColumn = useMemo(() => {
    if (!mobile?.badgeField) return null;
    return visibleColumns.find((col) => col.field === mobile.badgeField) ?? null;
  }, [visibleColumns, mobile?.badgeField]);

  const bodyColumns = useMemo(() => {
    if (hideBodyFields) return [];
    const excluded = new Set(hiddenFields);
    if (primaryColumn) excluded.add(primaryColumn.field);
    if (badgeColumn) excluded.add(badgeColumn.field);
    const remaining = visibleColumns.filter((col) => !excluded.has(col.field));
    if (!bodyOrder || bodyOrder.length === 0) return remaining;
    // `bodyOrder` (opt-in): listed fields first, in the given order; every
    // other body field keeps its grid column order after them. The collapsed
    // / expanded slices below both follow this order.
    const ordered = [];
    const byField = new Map(remaining.map((col) => [col.field, col]));
    bodyOrder.forEach((field) => {
      const col = byField.get(field);
      if (col) {
        ordered.push(col);
        byField.delete(field);
      }
    });
    remaining.forEach((col) => {
      if (byField.has(col.field)) ordered.push(col);
    });
    return ordered;
  }, [
    visibleColumns,
    hiddenFields,
    primaryColumn,
    badgeColumn,
    hideBodyFields,
    bodyOrder,
  ]);

  const collapsedColumns = useMemo(
    () => (collapseBody ? [] : bodyColumns.slice(0, collapsedAfter)),
    [bodyColumns, collapsedAfter, collapseBody],
  );
  const expandedColumns = useMemo(
    () => (collapseBody ? bodyColumns : bodyColumns.slice(collapsedAfter)),
    [bodyColumns, collapsedAfter, collapseBody],
  );

  // ---------------------------------------------------------------------------
  // Batch C wave 2 — card-mode sort/filter affordances + sort sheet.
  // Since 2026-10-01 (Amine) the Sort / Filter buttons are NOT a separate row
  // anymore: they render inside the merged control row next to the screen
  // toolbar's quick filter / ⋮ cluster (see the JSX below), one row of same-
  // sized 36×36 buttons on every card screen. The sheets themselves (sort /
  // filter drawers) are unchanged.
  // ---------------------------------------------------------------------------
  const hasSortControl = typeof onSortModelChange === "function";

  // ── Filter sheet (§3b): entries are fully screen-supplied — the wrapper only
  // renders the sheet shell around the screen's own context-free filter nodes.
  // Shape: { field, headerName, summary, node, active? }.
  const mobileFilters = useMemo(
    () =>
      Array.isArray(mobile?.filters)
        ? mobile.filters.filter((entry) => entry && entry.field)
        : [],
    [mobile?.filters],
  );

  // Badge count on the filter icon: the screen's `activeCount` wins; otherwise
  // count entries flagged `active: true`.
  const filterActiveCount =
    typeof mobile?.activeCount === "number" &&
    Number.isFinite(mobile.activeCount)
      ? mobile.activeCount
      : mobileFilters.filter((entry) => entry.active === true).length;

  const canClearAllFilters = typeof mobile?.onClearFilters === "function";

  // Screen-supplied filter chips: { key, label, onClear }.
  const mobileChips = useMemo(
    () => (Array.isArray(mobile?.chips) ? mobile.chips.filter(Boolean) : []),
    [mobile?.chips],
  );

  const activeSortEntry =
    Array.isArray(sortModel) && sortModel.length > 0 ? sortModel[0] : null;
  const activeSortField =
    activeSortEntry?.field ?? activeSortEntry?.columnField ?? null;
  const activeSortDirection =
    (activeSortEntry?.sort ?? activeSortEntry?.direction) === "desc"
      ? "desc"
      : "asc";
  const hasActiveSort = Boolean(activeSortField);

  // One row per visible, sortable column in the `columns` prop order — the
  // same set the desktop grid exposes, minus hiddenFields / header-less
  // entries. Single-column sort only, mirroring desktop header semantics so the
  // persisted sortModel round-trips between modes untouched.
  const sortSheetColumns = useMemo(() => {
    const excluded = new Set(hiddenFields);
    return visibleColumns.filter(
      (col) =>
        !excluded.has(col.field) &&
        col.sortable !== false &&
        Boolean(col.headerName),
    );
  }, [visibleColumns, hiddenFields]);

  const applySort = useCallback(
    (field, direction) => {
      if (typeof onSortModelChange !== "function") return;
      if (!field) {
        onSortModelChange([]);
        return;
      }
      onSortModelChange([
        { field, sort: direction === "desc" ? "desc" : "asc" },
      ]);
    },
    [onSortModelChange],
  );

  // Row tap makes the column *the* sort column (default ascending, per design
  // §3c); tapping the direction icon flips asc/desc. Re-applying the current
  // direction on an already-active row is a deliberate no-op.
  const handleSortRowTap = (col) => {
    const isActive = activeSortField === col.field;
    applySort(col.field, isActive ? activeSortDirection : "asc");
  };

  const toggleSortDirection = (col) => {
    applySort(col.field, activeSortDirection === "asc" ? "desc" : "asc");
  };

  const hasControlButtons = hasSortControl || mobileFilters.length > 0;

  // ---------------------------------------------------------------------------
  // Pipeline: rows -> quick filter -> sort -> page slice.
  // (Column filters are already applied by the screen before `rows` arrives.)
  // ---------------------------------------------------------------------------
  const canControlFilterModel = typeof onFilterModelChange === "function";

  const modelQuickFilterValues = useMemo(() => {
    const values = filterModel?.quickFilterValues;
    if (!Array.isArray(values)) return [];
    return values.filter((value) => String(value ?? "").trim() !== "");
  }, [filterModel]);

  const quickFilterValues = useMemo(() => {
    if (canControlFilterModel) return modelQuickFilterValues;
    return internalQuickFilter.trim() ? [internalQuickFilter] : [];
  }, [canControlFilterModel, modelQuickFilterValues, internalQuickFilter]);

  const quickFilterText = quickFilterValues[0] ?? "";
  const quickFilterKey = quickFilterValues.join("\u0000");

  const quickFilterMatchers = useMemo(
    () =>
      quickFilterValues
        .map((value) => ({ raw: value, normalized: normalizeText(value) }))
        .filter((matcher) => matcher.normalized),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quickFilterKey],
  );

  const rowMatchesQuickFilter = useCallback(
    (row, filterValue, normalizedNeedle) =>
      visibleColumns.some((col) => {
        if (col.filterable === false) return false;
        const value = getColumnValue(col, row);
        // Money-style columns can ship their own quick-filter applier
        // (currencyQuickFilterFn). Honor MUI's contract here so card search
        // and the desktop grid match the same strings — the card search used
        // to ignore this hook entirely (the Expenses Amount comma quirk).
        if (typeof col.getApplyQuickFilterFn === "function") {
          const apply = col.getApplyQuickFilterFn(filterValue, col, null);
          if (apply === null) return false;
          if (typeof apply === "function") {
            return apply(value, row, col, null) === true;
          }
        }
        const formatted = getColumnFormattedValue(col, row, value);
        return normalizeText(formatted).includes(normalizedNeedle);
      }),
    [visibleColumns],
  );

  const sortModelKey = useMemo(() => JSON.stringify(sortModel ?? []), [sortModel]);

  const processedRows = useMemo(() => {
    let next = rowsSafe;

    if (quickFilterMatchers.length > 0) {
      next = next.filter((row) =>
        quickFilterMatchers.every((matcher) =>
          rowMatchesQuickFilter(row, matcher.raw, matcher.normalized),
        ),
      );
    }

    if (Array.isArray(sortModel) && sortModel.length > 0) {
      const first = sortModel[0] || {};
      const sortColumn = visibleColumns.find(
        (col) => col.field === (first.field ?? first.columnField),
      );
      if (sortColumn) {
        const direction =
          (first.sort ?? first.direction) === "desc" ? -1 : 1;
        next = [...next].sort(
          (rowA, rowB) =>
            direction * compareColumnValues(sortColumn, rowA, rowB, rowIdOf),
        );
      }
    }

    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsSafe, quickFilterKey, sortModelKey, visibleColumns, rowMatchesQuickFilter, rowIdOf]);

  const visibleRows = useMemo(
    () => processedRows.slice(0, visibleCount),
    [processedRows, visibleCount],
  );

  // ---------------------------------------------------------------------------
  // Keep the screen's filtered-row bookkeeping correct without a grid: mirror
  // the grid's `onStateChange({ filter: { filteredRowsLookup } })` event. The
  // emitted key list is compared before firing so we cannot cause render loops.
  // ---------------------------------------------------------------------------
  const emittedLookupKeysRef = useRef(null);
  useEffect(() => {
    if (typeof onStateChange !== "function") return;
    const lookup = {};
    processedRows.forEach((row) => {
      lookup[String(rowIdOf(row))] = true;
    });
    const keys = Object.keys(lookup).sort();
    const previousKeys = emittedLookupKeysRef.current;
    const isSame =
      previousKeys &&
      previousKeys.length === keys.length &&
      previousKeys.every((key, index) => key === keys[index]);
    if (isSame) return;
    emittedLookupKeysRef.current = keys;
    onStateChange({ filter: { filteredRowsLookup: lookup } });
  }, [processedRows, onStateChange, rowIdOf]);

  // ---------------------------------------------------------------------------
  // Filter / sort / search changes reset paging and scroll back to the top.
  // ---------------------------------------------------------------------------
  const pipelineKey = `${quickFilterKey}::${sortModelKey}`;
  const previousPipelineKeyRef = useRef(pipelineKey);
  useEffect(() => {
    if (previousPipelineKeyRef.current === pipelineKey) return;
    previousPipelineKeyRef.current = pipelineKey;
    setVisibleCount(pageSize);
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = 0;
    }
  }, [pipelineKey, pageSize]);

  // ---------------------------------------------------------------------------
  // Card scroll memory (replaces the desktop virtual-scroller restore, which
  // no-ops here because there is no grid root element).
  // ---------------------------------------------------------------------------
  const scrollKeyRef = useRef(null);
  if (scrollKeyRef.current === null) {
    scrollKeyRef.current =
      mobile?.scrollKey ||
      (typeof window !== "undefined" ? window.location.pathname : "cards");
  }
  const scrollKey = scrollKeyRef.current;

  useEffect(
    () => () => {
      const element = scrollerRef.current;
      if (element && element.scrollTop > 0) {
        CARD_SCROLL_MEMORY.set(scrollKey, element.scrollTop);
      } else {
        CARD_SCROLL_MEMORY.delete(scrollKey);
      }
    },
    [scrollKey],
  );

  const restoredScrollRef = useRef(false);
  useEffect(() => {
    if (restoredScrollRef.current) return;
    const saved = CARD_SCROLL_MEMORY.get(scrollKey);
    if (!saved) return;
    const element = scrollerRef.current;
    const hasContent = rowsSafe.length > 0 || loading !== true;
    if (!element || !hasContent) return;
    const raf = requestAnimationFrame(() => {
      const maxScroll = element.scrollHeight - element.clientHeight;
      element.scrollTop = Math.max(0, Math.min(saved, maxScroll));
      restoredScrollRef.current = true;
    });
    return () => cancelAnimationFrame(raf);
  }, [scrollKey, loading, rowsSafe.length]);

  // ---------------------------------------------------------------------------
  // Selection + taps
  // ---------------------------------------------------------------------------
  const toggleSelection = useCallback(
    (id) => {
      if (typeof onRowSelectionModelChange !== "function") return;
      const current = selectionToArray(rowSelectionModel);
      const exists = current.some((entry) => String(entry) === String(id));
      const next = exists
        ? current.filter((entry) => String(entry) !== String(id))
        : [...current, id];
      const keepShape = !rowSelectionModel || Array.isArray(rowSelectionModel);
      onRowSelectionModelChange(
        keepShape ? next : { ...rowSelectionModel, ids: next },
      );
    },
    [onRowSelectionModelChange, rowSelectionModel],
  );

  const handleCardTap = useCallback(
    (row, nativeEvent) => {
      const id = rowIdOf(row);

      // Mirror MUI's `GridRow` click contract EXACTLY, in this order:
      //   1. `onRowClick(params, event, details)` runs first, with the click
      //      event as its 2nd argument;
      //   2. the built-in selection handling runs only when the screen has not
      //      set `event.defaultMuiPrevented`.
      // Both halves matter. BugReports' `onRowClick` sets
      // `event.defaultMuiPrevented = true` on the synthetic click that follows
      // a long-press (a TypeError when no event argument is passed), and with
      // the toggle running first the wrapper deselected the very row the
      // long-press had just selected. Invoices relies on
      // `disableRowSelectionOnClick={selectionEnabled}` and toggles inside its
      // own `onRowClick`, so the guard below must stay untouched — a screen
      // that handles selection itself is never double-toggled.
      // React synthetic events are plain mutable objects (no pooling since
      // React 17), so initializing the MUI flag on the real event is safe and
      // keeps `target` / `currentTarget` / `stopPropagation` working for the
      // screens' handlers.
      let cardEvent = nativeEvent;
      if (!cardEvent || typeof cardEvent !== "object") {
        cardEvent = {};
      }
      cardEvent.defaultMuiPrevented = false;

      if (typeof onRowClick === "function") {
        onRowClick(
          {
            id,
            row,
            field: undefined,
            colDef: undefined,
            columns: visibleColumns,
            api: null,
          },
          cardEvent,
        );
      }

      if (cardEvent.defaultMuiPrevented) return;

      // Blanket tap rule (2026-10-05): the wrapper only auto-toggles while
      // selection is ARMED — a not-armed tap never selects here; it belongs
      // to the screen's `onRowClick` (open the row's detail).
      if (selectionArmed && !disableRowSelectionOnClick) {
        toggleSelection(id);
      }
    },
    [
      rowIdOf,
      selectionArmed,
      disableRowSelectionOnClick,
      toggleSelection,
      onRowClick,
      visibleColumns,
    ],
  );

  const handleQuickFilterChange = useCallback(
    (nextValue) => {
      const nextValues = nextValue ? [nextValue] : [];
      if (canControlFilterModel) {
        onFilterModelChange({ ...(filterModel || {}), quickFilterValues: nextValues });
      } else {
        setInternalQuickFilter(nextValue || "");
      }
    },
    [canControlFilterModel, filterModel, onFilterModelChange],
  );

  // Double-tap open was REMOVED 2026-10-05 (Amine): no screen sets
  // `mobile.doubleTapToOpen` anymore — one tap opens the detail unless
  // selection is armed (see `handleCardTap` / `selectionArmed`). The screens'
  // `onRowDoubleClick` props stay desktop-only (the real grid still opens on
  // double-click there).

  // hideNAFields (opt-in): a body field whose formatted value is the "N/A"
  // placeholder is dropped for that row — Invoices' "Late By" (Amine: show it
  // "only if it isn't N/A"). Evaluated per row, so the expander count stays
  // truthful card by card. SCOPE (2026-10-05): `true` applies to every body
  // field; an array of field names applies only to those fields — Invoices
  // scopes it to "Late By" so the Note column always survives into the
  // expanded set and the "Show N more" button renders on every card.
  const isRenderableBodyField = useCallback(
    (col, row) => {
      if (!hideNAFieldsAll && !(hideNAFieldsList?.includes(col.field))) {
        return true;
      }
      const value = getColumnValue(col, row);
      const formatted = getColumnFormattedValue(col, row, value);
      if (formatted === null || formatted === undefined) return true;
      return !(typeof formatted === "string" && formatted.trim() === "N/A");
    },
    [hideNAFieldsAll, hideNAFieldsList],
  );

  // ---------------------------------------------------------------------------
  // Rendering helpers
  // ---------------------------------------------------------------------------
  const renderFieldRow = (col, row, rowId) => {
    const value = getColumnValue(col, row);
    const formatted = getColumnFormattedValue(col, row, value);
    // Class-list parity with the desktop cell: the column's own `cellClassName`
    // / `className` (MUI applies both to body cells — `cellClassName` is the
    // deprecated alias) plus the screen's `getCellClassName` function output.
    // Screens style these from the grid container's sx (e.g. Members'
    // `.member-avatar-cell` rule), so they must keep matching card descendants.
    const columnCellClassName =
      typeof getCellClassName === "function"
        ? getCellClassName({ row, value, field: col.field, colDef: col, id: rowId })
        : "";
    const cellClassName = [col.cellClassName, col.className, columnCellClassName]
      .filter(Boolean)
      .join(" ");
    const hasRenderCell = typeof col.renderCell === "function";
    const stacked =
      multilineFields.includes(col.field) ||
      (!hasRenderCell &&
        typeof formatted === "string" &&
        formatted.length > MULTILINE_CHAR_THRESHOLD);
    const valueContent = hasRenderCell ? (
      col.renderCell(buildCellParams(col, row, rowId, value, formatted))
    ) : (
      <Typography
        variant="body2"
        component="span"
        sx={{
          fontWeight: 500,
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          whiteSpace: stacked ? "pre-wrap" : "normal",
        }}
      >
        {formatted === null || formatted === undefined ? "" : String(formatted)}
      </Typography>
    );

    // `gridClasses.cell` is applied so screen-level sx rules written against
    // `.MuiDataGrid-cell` (e.g. Invoices' `.MuiDataGrid-cell.notAvailable`)
    // keep matching card descendants.
    return (
      <Box
        key={col.field}
        sx={{
          display: "flex",
          flexDirection: stacked ? "column" : "row",
          alignItems: stacked ? "flex-start" : "center",
          gap: stacked ? 0 : 1,
          mb: 0.5,
          minWidth: 0,
        }}
      >
        <Typography
          variant={stacked ? "caption" : "body2"}
          sx={{ color: "text.secondary", flexShrink: 0 }}
        >
          {col.headerName || col.field}
        </Typography>
        <Box
          className={[gridClasses.cell, cellClassName].filter(Boolean).join(" ")}
          onClick={stopIfInteractiveTarget}
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: stacked ? "flex-start" : "flex-end",
            textAlign: stacked ? "left" : "right",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
            "& > *": { maxWidth: "100%" },
          }}
        >
          {valueContent}
        </Box>
      </Box>
    );
  };

  const buildBadge = (row, rowId) => {
    if (!badgeColumn) return null;
    const value = getColumnValue(badgeColumn, row);
    const formatted = getColumnFormattedValue(badgeColumn, row, value);
    return (
      <Box
        onClick={stopIfInteractiveTarget}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexShrink: 0,
          maxWidth: "55%",
          minHeight: 24,
          "& > *": { maxWidth: "100%" },
        }}
      >
        {typeof badgeColumn.renderCell === "function"
          ? badgeColumn.renderCell(buildCellParams(badgeColumn, row, rowId, value, formatted))
          : formatted !== null && formatted !== undefined && formatted !== "" && (
              <Chip size="small" variant="outlined" label={String(formatted)} />
            )}
      </Box>
    );
  };

  const buildHeadline = (row, rowId) => {
    if (!primaryColumn) return `#${rowId}`;
    const value = getColumnValue(primaryColumn, row);
    const formatted = getColumnFormattedValue(primaryColumn, row, value);
    if (formatted === null || formatted === undefined || formatted === "") {
      return `#${rowId}`;
    }
    return String(formatted);
  };

  // ---------------------------------------------------------------------------
  // Screen toolbar / states
  // ---------------------------------------------------------------------------
  const ToolbarComponent = slots?.toolbar;
  const toolbarSlotProps = slotProps?.toolbar ?? {};
  // Checkboxes render exactly while selection is armed — the same signal the
  // wrapper's tap contract uses (see `selectionArmed` above).
  const showCheckbox = selectionArmed;
  const hasExternalFilterHint = toolbarSlotProps?.hasAnyActiveFilters === true;

  // Clear-filters plumbing (finding B2). When the screen controls the filter
  // model (`onFilterModelChange` provided) its `onClear` already resets the
  // quick filter, so it is passed through untouched. When the WRAPPER owns the
  // quick filter, the screen's `onClear` can only clear its own column filters
  // — a card-mode search that leaves zero results would strand the user on
  // "No results" — so the handler handed to the screen's toolbar AND to the
  // cards' empty state also resets the internal quick filter.
  const screenOnClear =
    typeof toolbarSlotProps?.onClear === "function"
      ? toolbarSlotProps.onClear
      : undefined;
  const wrapperOwnsQuickFilter = !canControlFilterModel;

  const handleClearFilters = useCallback(() => {
    if (wrapperOwnsQuickFilter) {
      setInternalQuickFilter("");
    }
    if (typeof screenOnClear === "function") {
      screenOnClear();
    }
  }, [wrapperOwnsQuickFilter, screenOnClear]);

  let onClearFilters;
  if (typeof screenOnClear === "function") {
    onClearFilters = wrapperOwnsQuickFilter ? handleClearFilters : screenOnClear;
  } else if (wrapperOwnsQuickFilter && internalQuickFilter.trim() !== "") {
    // No screen clear handler, but the wrapper's own search is filtering the
    // list — the empty state still needs a Clear button that works.
    onClearFilters = handleClearFilters;
  } else {
    onClearFilters = undefined;
  }

  const isLoading = loading === true;
  const hasRows = rowsSafe.length > 0;
  const showSkeletons = isLoading && !hasRows;
  const showRefetchProgress = isLoading && hasRows;
  const filteredToZero = hasRows
    ? processedRows.length === 0
    : hasExternalFilterHint || quickFilterValues.length > 0;

  const totalCount = processedRows.length;
  const shownCount = visibleRows.length;
  const allVisible = shownCount >= totalCount;

  const rowRenderProps = slotProps?.row ?? {};

  let listContent;
  if (showSkeletons) {
    listContent = (
      <>
        {[0, 1, 2, 3].map((index) => (
          <SkeletonCard key={index} />
        ))}
      </>
    );
  } else if (!hasRows && !isLoading) {
    listContent = (
      <CardsEmptyState
        filtered={filteredToZero}
        localeText={localeText}
        onClearFilters={onClearFilters}
      />
    );
  } else if (filteredToZero) {
    listContent = (
      <CardsEmptyState
        filtered
        localeText={localeText}
        onClearFilters={onClearFilters}
      />
    );
  } else {
    listContent = (
      <>
        {visibleRows.map((row) => {
          const rowId = rowIdOf(row);
          // Per-row body fields: hideNAFields can drop an "N/A" field for one
          // card and keep it for the next — the expander count follows.
          const rowCollapsedColumns = collapsedColumns.filter((col) =>
            isRenderableBodyField(col, row),
          );
          const rowExpandedColumns = expandedColumns.filter((col) =>
            isRenderableBodyField(col, row),
          );
          const collapsedContent = rowCollapsedColumns.map((col) =>
            renderFieldRow(col, row, rowId),
          );
          const expandedContent = rowExpandedColumns.map((col) =>
            renderFieldRow(col, row, rowId),
          );
          return (
            <MobileCard
              key={String(rowId)}
              rowId={rowId}
              row={row}
              headline={buildHeadline(row, rowId)}
              badge={buildBadge(row, rowId)}
              collapsedContent={collapsedContent}
              expandedContent={expandedContent}
              expandedCount={rowExpandedColumns.length}
              rowHandlers={{
                onContextMenu: rowRenderProps?.onContextMenu,
                onTouchStart: rowRenderProps?.onTouchStart,
                onTouchEnd: rowRenderProps?.onTouchEnd,
                onTouchCancel: rowRenderProps?.onTouchCancel,
                // Round-7 (Amine 2026-10-05): on a coarse-pointer (touch)
                // device the screens' mouse long-press handlers are NOT
                // forwarded — the synthetic mousedown that follows every
                // tap restarted their 500ms timer. Touch handlers always
                // carry the mobile long-press; fine-pointer devices keep
                // the mouse path (MobileCard's own handlers no-op safely on
                // undefined via `?.`).
                onMouseDown: isCoarsePointer ? undefined : rowRenderProps?.onMouseDown,
                onMouseUp: isCoarsePointer ? undefined : rowRenderProps?.onMouseUp,
                onMouseLeave: isCoarsePointer ? undefined : rowRenderProps?.onMouseLeave,
                cursor: rowRenderProps?.style?.cursor,
              }}
              showCheckbox={showCheckbox}
              selected={selectionIncludes(rowSelectionModel, rowId)}
              onToggleSelect={() => toggleSelection(rowId)}
              onCardTap={(event) => handleCardTap(row, event)}
              cardActions={cardActions}
              contextMenuButton={contextMenuButton}
            />
          );
        })}
        <Box sx={{ pt: 0.5, pb: 2 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", textAlign: "center", mb: 1 }}
          >
            {allVisible ? `${totalCount} items` : `Showing ${shownCount} of ${totalCount}`}
          </Typography>
          {!allVisible && (
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setVisibleCount((previous) => previous + pageSize)}
              sx={{ minHeight: 40, textTransform: "none" }}
            >
              Load more
            </Button>
          )}
        </Box>
      </>
    );
  }

  const rootSx = useMemo(() => {
    const screenSx = Array.isArray(sx) ? sx : sx ? [sx] : [];
    return [
      ...screenSx,
      {
        height: "100%",
        width: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      },
    ];
  }, [sx]);

  return (
    <Box sx={rootSx}>
      {ToolbarComponent || hasControlButtons ? (
        // ONE card-mode control row (Amine 2026-10-05): the wrapper's Sort /
        // Filter buttons sit LEFT and the screen toolbar (quick filter + Grid
        // Options ⋮) fills the remaining width on the RIGHT — the toolbar's
        // own right-aligned cluster therefore lands on the row's right edge,
        // on every card screen. One non-wrapping row of same-sized 36×36
        // buttons (cardControlButtonSx mirrors the toolbar recipe). The
        // toolbar host stays a plain block so the toolbar keeps full width
        // (its internal flex spacers still split its own content); it only
        // neutralizes the toolbar container's asymmetric MUI default padding
        // (`padding: 4px 4px 0`) so the toolbar's buttons sit exactly on the
        // row's vertical center instead of 2px low — the
        // `& > .MuiDataGrid-toolbarContainer` override is card-mode-only
        // (this row never renders on desktop). No explicit bgcolor on
        // purpose: the row inherits the screen's grid container, so it
        // matches the toolbar's own background on every grid.
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 0.5,
            py: 0.25,
            borderBottom: 1,
            borderColor: "divider",
            minWidth: 0,
          }}
        >
          {hasControlButtons && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              {hasSortControl && (
                <Tooltip title="Sort by">
                  <IconButton
                    size="small"
                    aria-label="Sort by"
                    aria-pressed={hasActiveSort}
                    onClick={() => setSortSheetOpen(true)}
                    sx={(theme) => cardControlButtonSx(theme, hasActiveSort)}
                  >
                    <Badge
                      color="primary"
                      variant="dot"
                      overlap="circular"
                      invisible={!hasActiveSort}
                    >
                      <SortRoundedIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
              {mobileFilters.length > 0 && (
                <Tooltip title="Filters">
                  <IconButton
                    size="small"
                    aria-label="Filters"
                    onClick={() => setFilterSheetOpen(true)}
                    sx={(theme) =>
                      cardControlButtonSx(theme, filterActiveCount > 0)
                    }
                  >
                    <Badge
                      color="primary"
                      badgeContent={filterActiveCount}
                      max={99}
                      invisible={filterActiveCount === 0}
                    >
                      <FilterListRoundedIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {ToolbarComponent ? (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                "& > .MuiDataGrid-toolbarContainer": { pt: 0 },
              }}
            >
              <GridRootPropsContext.Provider value={CARD_MODE_ROOT_PROPS}>
                <ToolbarComponent
                  {...toolbarSlotProps}
                  isMobileToolbar
                  cardMode
                  quickFilterValue={quickFilterText}
                  onQuickFilterChange={handleQuickFilterChange}
                  onClear={onClearFilters}
                />
              </GridRootPropsContext.Provider>
            </Box>
          ) : (
            <Box sx={{ flex: 1, minWidth: 0 }} />
          )}
        </Box>
      ) : null}

      {showRefetchProgress && (
        <LinearProgress sx={{ height: 2, flexShrink: 0 }} />
      )}

      {(quickFilterText || mobileChips.length > 0) && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            pb: 0.5,
          }}
        >
          {quickFilterText ? (
            <Chip
              size="small"
              variant="outlined"
              label={`Search: "${quickFilterText}"`}
              onDelete={() => handleQuickFilterChange("")}
              sx={CARD_CHIP_SX}
            />
          ) : null}
          {mobileChips.map((chip) => (
            <Chip
              key={chip.key ?? chip.label}
              size="small"
              variant="outlined"
              label={chip.label}
              onDelete={
                typeof chip.onClear === "function"
                  ? () => chip.onClear()
                  : undefined
              }
              sx={CARD_CHIP_SX}
            />
          ))}
        </Box>
      )}

      <Box
        ref={scrollerRef}
        aria-busy={isLoading ? true : undefined}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          p: 1,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {listContent}
      </Box>

      {mobile?.footer ? (
        // Non-shrinking footer pinned below the card scroller (bottom of the
        // card container). Screens pass a ReactNode — e.g. Invoices' totals
        // strip is the SAME element tree its desktop grid footer renders.
        <Box sx={{ flexShrink: 0 }}>{mobile.footer}</Box>
      ) : null}

      {/* Sort sheet (§3c): 48px rows, one per sortable visible column, live
          apply, sheet stays open. ModalProps.disableEnforceFocus is required
          because the screens' ColumnFilter poppers portal their search field
          to document.body — without it the Modal's focus enforcement yanks
          focus out of the field back into the drawer, breaking typing. */}
      <Drawer
        anchor="bottom"
        open={sortSheetOpen}
        onClose={() => setSortSheetOpen(false)}
        ModalProps={{ disableEnforceFocus: true }}
        slotProps={{ paper: { sx: SHEET_PAPER_SX } }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            maxHeight: SHEET_MAX_HEIGHT,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
              Sort by
            </Typography>
            <IconButton
              size="small"
              aria-label="Close"
              onClick={() => setSortSheetOpen(false)}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {sortSheetColumns.map((col) => {
              const isActive = activeSortField === col.field;
              return (
                <Box
                  key={col.field}
                  role="button"
                  tabIndex={0}
                  aria-label={`Sort by ${col.headerName}`}
                  aria-pressed={isActive}
                  onClick={() => handleSortRowTap(col)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSortRowTap(col);
                    }
                  }}
                  sx={{
                    height: 48,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    cursor: "pointer",
                    backgroundColor: isActive
                      ? "action.selected"
                      : "transparent",
                    color: isActive ? "primary.main" : "text.primary",
                    "&:not(:last-child)": {
                      borderBottom: 1,
                      borderColor: "divider",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    {col.headerName}
                  </Typography>
                  {isActive && (
                    <IconButton
                      size="small"
                      aria-label={
                        activeSortDirection === "asc"
                          ? "Sort descending"
                          : "Sort ascending"
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleSortDirection(col);
                      }}
                    >
                      {activeSortDirection === "asc" ? (
                        <ArrowUpwardRoundedIcon fontSize="small" />
                      ) : (
                        <ArrowDownwardRoundedIcon fontSize="small" />
                      )}
                    </IconButton>
                  )}
                </Box>
              );
            })}
            {sortSheetColumns.length === 0 && (
              <Typography
                variant="body2"
                sx={{ px: 2, py: 2, color: "text.secondary" }}
              >
                No sortable columns.
              </Typography>
            )}
          </Box>
          <Divider />
          <Box sx={{ p: 1.5, flexShrink: 0 }}>
            <Button
              fullWidth
              variant="outlined"
              disabled={!hasActiveSort}
              onClick={() => applySort(null)}
              sx={{ minHeight: 40, textTransform: "none" }}
            >
              Clear sort
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Filter sheet (§3b): one row per filterable column — headerName +
          summary on the left, the screen's own filter node right-aligned.
          Live apply; the sheet does not need to close. The nodes' poppers
          (zIndex 9999, backdrop 9998) paint above this sheet (Drawer default
          theme.zIndex.drawer = 1200). "Clear all" matches desktop's toolbar
          clear (column filters + quick filter). */}
      <Drawer
        anchor="bottom"
        open={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        ModalProps={{ disableEnforceFocus: true }}
        slotProps={{ paper: { sx: SHEET_PAPER_SX } }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            maxHeight: SHEET_MAX_HEIGHT,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
              Filters
            </Typography>
            {canClearAllFilters && filterActiveCount > 0 && (
              <Button
                size="small"
                onClick={mobile.onClearFilters}
                sx={{ textTransform: "none" }}
              >
                Clear all
              </Button>
            )}
            <IconButton
              size="small"
              aria-label="Close"
              onClick={() => setFilterSheetOpen(false)}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {mobileFilters.map((entry) => (
              <Box
                key={entry.field}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.5,
                  minHeight: 48,
                  "&:not(:last-child)": {
                    borderBottom: 1,
                    borderColor: "divider",
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: entry.active === true ? 600 : 400 }}
                  >
                    {entry.headerName || entry.field}
                  </Typography>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ display: "block", color: "text.secondary" }}
                  >
                    {entry.summary || ""}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {entry.node}
                </Box>
              </Box>
            ))}
            {mobileFilters.length === 0 && (
              <Typography
                variant="body2"
                sx={{ px: 2, py: 2, color: "text.secondary" }}
              >
                No filters available.
              </Typography>
            )}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

/**
 * Public wrapper: desktop renders the real DataGrid with every prop untouched,
 * mobile renders the card list from the same props.
 */
export default function DataGridResponsive(props) {
  const isMobile = !useMediaQuery(MOBILE_QUERY);
  const { mobile, ...gridProps } = props;

  if (!isMobile) {
    return <DataGrid {...gridProps} />;
  }

  return <DataGridResponsiveCards {...gridProps} mobile={mobile} />;
}
